/**
 * KAN-1003: Политика конфиденциальности сервиса "Сметчик".
 * Основа: утверждённая владельцем политика content-zavod (редакция 01.11.2026),
 * адаптирована под данные ИИ-операторов (базы контактов, переписка, звонки).
 * Оператор: ИП с ИНН и ОГРНИП, в реестре операторов ПДн Роскомнадзора.
 * Паспортные данные и адрес не публикуются.
 */
import type { LegalDocument } from './types';
import {
  LEGAL_EDITION,
  SELLER,
  SELLER_REQUISITES,
  SERVICE_NAME,
  SERVICE_SITE,
  SUPPORT_EMAIL_LINK,
  SUPPORT_PHONE_LINK,
  TERMS_LINK,
} from './support';

export const PRIVACY_DOCUMENT: LegalDocument = {
  title: 'Политика конфиденциальности',
  edition: LEGAL_EDITION,
  sections: [
    {
      heading: '1. Общие положения',
      paragraphs: [
        `1.1. Политика описывает, какие данные обрабатывает сервис "${SERVICE_NAME}" (сайт и личный кабинет ${SERVICE_SITE}, далее Сервис), зачем и как они защищены. Политика составлена по Федеральному закону от 27.07.2006 N 152-ФЗ "О персональных данных".`,
        `1.2. Оператор персональных данных: ${SELLER.fullName} (${SELLER_REQUISITES}), далее Оператор. Оператор внесён в реестр операторов, осуществляющих обработку персональных данных, Роскомнадзора (уведомление N ${SELLER.rknNotice}).`,
        [
          '1.3. Вопросы о данных и запросы на их удаление: ',
          SUPPORT_EMAIL_LINK,
          ', телефон ',
          SUPPORT_PHONE_LINK,
          '.',
        ],
        [
          '1.4. Регистрируясь в Сервисе, пользователь даёт согласие на обработку своих данных по этой Политике. Вместе с Политикой действует ',
          TERMS_LINK,
          '.',
        ],
      ],
    },
    {
      heading: '2. Какие данные обрабатываются',
      paragraphs: [
        '2.1. Данные учётной записи: имя, фамилия, email, название компании. Пароль хранится только в виде необратимого хэша.',
        '2.2. Данные, которые пользователь сам загружает или получает через Сервис: базы контактов своих клиентов (имена, телефоны, аккаунты в мессенджерах), переписка и записи звонков ИИ-оператора, карточки CRM. Для этих данных оператором является пользователь, Сервис обрабатывает их по его поручению (часть 3 статьи 6 152-ФЗ) и только для его задач.',
        '2.3. Сессии подключённых аккаунтов мессенджеров хранятся в зашифрованном виде. Остальные данные доступа к подключённым сервисам хранятся с ограниченным доступом.',
        '2.4. Данные об операциях: баланс, пополнения, списания. Данные банковской карты Сервис не получает и не хранит: оплата проходит на странице платёжного сервиса ЮKassa.',
        '2.5. Технические данные: время запросов, ошибки, данные браузера для входа в кабинет.',
      ],
    },
    {
      heading: '3. Зачем',
      paragraphs: [
        '3.1. Чтобы предоставлять Сервис: вести переписку и звонки ИИ-оператора, CRM и базы контактов по настройкам пользователя.',
        '3.2. Чтобы присылать уведомления о работе кабинета и оплате, отвечать в поддержке.',
        '3.3. Чтобы учитывать баланс, пополнения и списания, отправлять кассовые чеки.',
        '3.4. Чтобы разбирать ошибки и улучшать Сервис.',
      ],
    },
    {
      heading: '4. Передача третьим лицам',
      paragraphs: [
        '4.1. Для работы функций Сервиса данные передаются поставщикам, которые эти функции выполняют, в нужном объёме: поставщики нейросетей для обработки текста и голоса, операторы телефонии, мессенджеры, через которые идёт переписка. Часть поставщиков нейросетей находится за пределами России: передача нужна для выполнения действий, которые настроил пользователь.',
        '4.2. Платёжные данные обрабатывают платёжный сервис ЮKassa и банк, через которые прошла оплата.',
        '4.3. В остальных случаях данные передаются только по требованию закона или с согласия пользователя. Данные не продаются и не используются для рекламы.',
      ],
    },
    {
      heading: '5. Хранение и защита',
      paragraphs: [
        '5.1. Данные хранятся на серверах хостинг-провайдера, расположенных в России (Timeweb Cloud). При обработке ИИ данные могут передаваться поставщикам нейросетей по пункту 4.1.',
        '5.2. Доступ к кабинету защищён паролем, соединение с сайтом шифруется. Доступ Оператора к данным кабинета есть только для поддержки и разбора ошибок.',
        '5.3. Данные хранятся, пока пользователь пользуется Сервисом, и удаляются по его запросу в поддержку, кроме данных, которые закон требует хранить дольше (например, сведения о платежах).',
      ],
    },
    {
      heading: '6. Права пользователя',
      paragraphs: [
        [
          '6.1. Пользователь может узнать, какие его данные обрабатываются, исправить их, отозвать согласие и потребовать удаления: письмом на ',
          SUPPORT_EMAIL_LINK,
          '. Ответ в течение 10 рабочих дней.',
        ],
      ],
    },
    {
      heading: '7. Изменения',
      paragraphs: ['7.1. Новая редакция Политики публикуется на этой странице и действует с момента публикации.'],
    },
    {
      heading: '8. Реквизиты и контакты',
      paragraphs: [
        `8.1. ${SELLER.fullName}, ${SELLER_REQUISITES}.`,
        ['8.2. Email ', SUPPORT_EMAIL_LINK, ', телефон ', SUPPORT_PHONE_LINK, '.'],
      ],
    },
  ],
};
